import { useCallback } from 'react';
import { Kebab, Pencil, Trash, Folder, Plus } from '../../lib/icons.tsx';
import AccentSwatches from '../../canvas/AccentSwatches.tsx';
import { useMenuKeys } from '../../lib/useMenuKeys.ts';
import { useDismissMenu } from '../../lib/useDismissMenu.ts';
import type { Dispatch, SetStateAction } from 'react';
import type { LibraryItem } from '../../../shared/api.ts';

/** Everything a library item's kebab menu can do. Held as one object so a tile
 *  takes one prop rather than six, the way `DeckCardActions` does on the decks
 *  page. */
export type LibraryItemActions = {
  onEdit: (item: LibraryItem) => void;
  /** The card's Insert button. NOT a menu item: the kebab's six verbs are the
   *  item's contract with the user and Run 10 left them alone. */
  onInsert: (item: LibraryItem) => void;
  onAddSlide: (item: LibraryItem) => void;
  onRename: (item: LibraryItem) => void;
  onMove: (item: LibraryItem) => void;
  onRecolour: (item: LibraryItem, colour: string) => void;
  onDelete: (item: LibraryItem) => void;
};

type LibraryItemMenuProps = {
  item: LibraryItem;
  /** The page's single open-menu slot, so opening one closes any other. */
  menuOpen: boolean;
  busy: boolean;
  setMenuKey: Dispatch<SetStateAction<string | null>>;
  actions: LibraryItemActions;
};

/**
 * A library item's kebab trigger and the menu it opens. Lifted out of
 * `Library.tsx` on 2026-09-07 (Run 8) unchanged: the six verbs and their exact
 * wording are the item's contract with the user, and the page they lived in was
 * about to be rebuilt around them.
 *
 * The keyboard is `useMenuKeys`, the same hook the decks page's `DeckMenu`
 * uses (Run 11): focus on open, arrows to walk, and the caret back on the
 * kebab on Escape or a pick. This menu is why that hook defers its Tab
 * dismissal by a frame — the accent swatches inside it are a radiogroup rather
 * than menu items, and closing on the keydown shut the menu on the way into
 * them.
 *
 * Click-away is `useDismissMenu`, with BOTH the kebab and the menu counted as
 * inside: it has a containment test, so the colour panel's custom hex field
 * can be clicked without closing its own host, which the old document-wide
 * mousedown could not manage without a stopPropagation on every control.
 */
export default function LibraryItemMenu({ item, menuOpen, busy, setMenuKey, actions }: LibraryItemMenuProps) {
  const menuKey = `item:${item.slug}`;
  const close = useCallback(() => setMenuKey(null), [setMenuKey]);
  const { triggerId, btnRef, menuRef, onMenuKeyDown } = useMenuKeys(menuOpen, close);
  useDismissMenu([btnRef, menuRef], close, menuOpen);
  return (
    <>
      <button
        ref={btnRef}
        id={triggerId}
        type="button"
        className="iconbtn"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`Actions for ${item.name}`}
        onClick={(e) => {
          e.stopPropagation();
          setMenuKey((p) => (p === menuKey ? null : menuKey));
        }}
      >
        <Kebab />
      </button>
      {menuOpen && (
        <div
          ref={menuRef}
          className="menu"
          role="menu"
          // Labelled BY the trigger rather than repeating the item's name: two
          // labels reading "Actions for Morgan" is one fact announced twice.
          aria-labelledby={triggerId}
          onKeyDown={onMenuKeyDown}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenuKey(null);
              actions.onEdit(item);
            }}
          >
            <Pencil /> <span>Edit {item.count > 1 ? 'slides' : 'slide'}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            disabled={busy}
            onClick={() => {
              setMenuKey(null);
              actions.onAddSlide(item);
            }}
          >
            <Plus /> <span>Add a slide…</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            disabled={busy}
            onClick={() => {
              setMenuKey(null);
              actions.onRename(item);
            }}
          >
            <Pencil /> <span>Rename item…</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            disabled={busy}
            onClick={() => {
              setMenuKey(null);
              actions.onMove(item);
            }}
          >
            <Folder /> <span>Move to category…</span>
          </button>
          <div className="menu__item menu__item--static">
            <AccentSwatches
              value={item.color}
              onPick={(c, meta) => {
                if (!meta?.custom) setMenuKey(null);
                if (c !== item.color) actions.onRecolour(item, c);
              }}
              disabled={busy}
            />
          </div>
          <hr className="menu__sep" />
          <button
            type="button"
            role="menuitem"
            className="menu__item menu__item--destructive"
            disabled={busy}
            onClick={() => {
              setMenuKey(null);
              actions.onDelete(item);
            }}
          >
            <Trash /> <span>Remove from library</span>
          </button>
        </div>
      )}
    </>
  );
}
